import styled, { css } from 'styled-components'
import { Spacings, Text, Devices } from '@styles'

export const BaseWrapperV2 = styled.div`
	padding: ${Spacings.SPACING_4B};
	background: ${({ theme }) => theme.colors.surface.card};
	border-radius: ${({ theme }) => theme.radius.lg};
	box-shadow: ${({ theme }) => theme.shadows.sm};
`

export const OutletWrapperV2 = styled.section`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	height: 100%;
`

export const PageHeaderV2 = styled.h1`
	font-weight: ${Text.BOLD_700};
	font-size: ${Text.XX_LARGE};
	color: ${({ theme }) => theme.colors.text.primary};
	margin-bottom: ${Spacings.SPACING_4B};
`

export const SectionHeaderV2 = styled.h2`
	font-weight: ${Text.BOLD_600};
	font-size: ${Text.X_LARGE};
	color: ${({ theme }) => theme.colors.text.primary};
	padding-bottom: ${Spacings.SPACING_3B};
	margin-bottom: ${Spacings.SPACING_4B};
	border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
	display: flex;
	align-items: center;
	justify-content: space-between;
`

export const FormElementContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_1B};
	width: 100%;
`

const getLabelColor = ({ theme, disabled }) => {
	if (disabled) {
		return theme.colors.text.disabled
	}
	return theme.colors.text.secondary
}

export const Label = styled.label`
	font-size: ${Text.EXTRA_SMALL};
	color: ${getLabelColor};
	font-weight: ${Text.BOLD_600};
`

export const ErrorMessage = styled.span`
	font-size: ${Text.EXTRA_SMALL};
	color: ${({ theme }) => theme.colors.status.error.text};
	margin-top: ${Spacings.SPACING_2};
`

export const baseInputStyles = css`
	width: 100%;
	padding: ${Spacings.SPACING_6} ${Spacings.SPACING_3B};
	font-size: ${Text.SMALL};
	border: 1px solid ${({ theme, $hasError }) => ($hasError ? theme.colors.status.error.border : theme.colors.border.default)};
	border-radius: ${({ theme }) => theme.radius.sm};
	color: ${({ theme }) => theme.colors.text.primary};
	background: ${({ theme, disabled }) => (disabled ? theme.colors.surface.sunken : theme.colors.surface.card)};
	font-weight: ${Text.BOLD_500};
	font-family: inherit;
	transition:
		border-color 0.2s ease,
		box-shadow 0.2s ease;

	@media ${Devices.tablet} {
		padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_3B};
		font-size: ${Text.MEDIUM};
	}

	&:focus {
		border-color: ${({ theme, $hasError }) => ($hasError ? theme.colors.status.error.border : theme.colors.border.focus)};
		outline: none;
		box-shadow: 0 0 0 3px ${({ theme, $hasError }) => ($hasError ? theme.colors.status.error.bg : theme.colors.brand.subtle)};
	}

	&:disabled {
		cursor: not-allowed;
		color: ${({ theme }) => theme.colors.text.greyThree};
	}

	&::placeholder {
		color: ${({ theme }) => theme.colors.text.secondary};
		font-weight: ${Text.REGULAR};
		font-size: ${({ $placeholderFontSize }) => $placeholderFontSize || Text.SMALL};
		opacity: 1;
	}
`

/* ─── Vertical tab rail ────────────────────────────────────────────────── */

export const VerticalTabsWrapperV2 = styled.nav`
	position: sticky;
	top: ${({ $top }) => $top || Spacings.SPACING_4B};
	align-self: flex-start;
	display: flex;
	flex-direction: column;
	align-items: stretch;
	flex-shrink: 0;
	gap: ${Spacings.SPACING_2B};
	width: ${({ $width }) => $width || 'fit-content'};
`

// One box in the rail: icon stacked over the label on tablet+, side by side on mobile.
export const VerticalTabBoxV2 = styled.button`
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
	gap: ${Spacings.SPACING_2};
	padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_2B};
	cursor: pointer;
	overflow: hidden;
	font-family: ${({ theme }) => theme.fontFamily?.inter || "'Inter', sans-serif"};
	font-size: ${Text.EXTRA_SMALL};
	font-weight: ${Text.BOLD_600};
	text-align: center;
	border-radius: ${({ theme }) => theme.radius.lg};
	transition: all 0.2s ease-in-out;

	border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.brand.primary : theme.colors.border.default)};
	background: ${({ theme, $active }) => ($active ? theme.colors.brand.primary : theme.colors.surface.card)};
	color: ${({ theme, $active }) => ($active ? theme.colors.text.inverse : theme.colors.text.secondary)};
	box-shadow: ${({ theme, $active }) => ($active ? theme.shadows.sm : 'none')};

	& > span {
		width: 4rem;
	}

	svg {
		font-size: ${Text.X_LARGE};
		color: inherit;
	}

	&:hover {
		border-color: ${({ theme }) => theme.colors.brand.primary};
		background: ${({ theme, $active }) => ($active ? theme.colors.brand.primary : theme.colors.brand.subtle)};
		color: ${({ theme, $active }) => ($active ? theme.colors.text.inverse : theme.colors.brand.primary)};
	}

	@media ${Devices.tablet} {
		flex-direction: column;
		gap: ${Spacings.SPACING_1B};

		& > span {
			width: 4.5rem;
		}
	}
`
